
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { getApprovedCampaigns } from "../services/campaignService";

import Navbar from "../components/Navbar";
import SecondNavbar from "../components/SecondNavbar";
import Footer from "../components/Footer";

import "./Campaign.css";

// =========================================================
// DEFAULT CAMPAIGNS
// =========================================================

const defaultCampaigns = [
  {
    id: "default-1",
    title: "Tree Plantation Drive",
    image:
      "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1200",
    location: "Lahore",
    date: "20 July 2026",
    volunteers: 3,
    organizer: "EcoVibe Community",
    status: "Ongoing",
    description:
      "The Tree Plantation Drive aims to make Pakistan greener by planting trees in schools, parks, roadsides, and public spaces.",
  },
  {
    id: "default-2",
    title: "Avoid Plastic Campaign",
    image:
      "https://images.unsplash.com/photo-1621451537084-482c73073a0f?w=1200",
    location: "Faisalabad",
    date: "28 July 2026",
    volunteers: 3,
    organizer: "EcoVibe Volunteers",
    status: "Ongoing",
    description:
      "The Avoid Plastic Campaign spreads awareness about the harmful effects of single-use plastic and encourages sustainable alternatives.",
  },
  {
    id: "default-3",
    title: "Safe Sutra Punjab",
    image:
      "https://images.unsplash.com/photo-1528740561666-dc2479dc08ab?w=1200",
    location: "Gujranwala",
    date: "05 August 2026",
    volunteers: 3,
    organizer: "Punjab Green Team",
    status: "Ongoing",
    description:
      "Safe Sutra Punjab is a community clean-up campaign focused on keeping streets, public spaces, and parks clean.",
  },
];

// =========================================================
// CONSTANTS
// =========================================================

// Total approved/current community volunteers
// This is separate from campaign.volunteers.
const TOTAL_COMMUNITY_VOLUNTEERS = 4;

// =========================================================
// HELPER FUNCTIONS
// =========================================================

const formatDate = (date) => {
  if (!date) return "N/A";

  if (
    typeof date === "string" &&
    date.includes(" ")
  ) {
    return date;
  }

  try {
    return new Date(date).toLocaleDateString(
      "en-GB",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }
    );
  } catch {
    return "N/A";
  }
};

const getImageUrl = (image) => {
  if (!image) {
    return "https://images.unsplash.com/photo-1497250681960-ef046c08a56e?w=1200";
  }

  if (
    typeof image === "string" &&
    image.startsWith("http")
  ) {
    return image;
  }

  return `/uploads/${image}`;
};

// =========================================================
// CAMPAIGN COMPONENT
// =========================================================

const Campaign = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] =
    useState("All");
  const [loading, setLoading] = useState(true);

  // =======================================================
  // FETCH CAMPAIGNS
  // =======================================================

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);

      const res =
        await getApprovedCampaigns();

      console.log(
        "APPROVED CAMPAIGNS:",
        res
      );

      if (res?.success) {
        setCampaigns(
          Array.isArray(res.campaigns)
            ? res.campaigns
            : []
        );
      } else {
        setCampaigns([]);
      }
    } catch (error) {
      console.error(
        "Campaign Error:",
        error
      );

      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  // =======================================================
  // ALL CAMPAIGNS
  // =======================================================

  const allCampaigns = useMemo(() => {
    return [
      ...defaultCampaigns,
      ...campaigns,
    ];
  }, [campaigns]);

  // =======================================================
  // FILTER CAMPAIGNS
  // =======================================================

  const filteredCampaigns = useMemo(() => {
    const keyword =
      search.toLowerCase().trim();

    return allCampaigns.filter(
      (campaign) => {
        const matchesSearch =
          campaign.title
            ?.toLowerCase()
            .includes(keyword) ||
          campaign.location
            ?.toLowerCase()
            .includes(keyword) ||
          campaign.description
            ?.toLowerCase()
            .includes(keyword) ||
          campaign.organizer
            ?.toLowerCase()
            .includes(keyword);

        const currentStatus =
          campaign.status ||
          "Ongoing";

        const matchesStatus =
          statusFilter === "All" ||
          currentStatus
            .toLowerCase() ===
            statusFilter.toLowerCase();

        return (
          matchesSearch &&
          matchesStatus
        );
      }
    );
  }, [
    allCampaigns,
    search,
    statusFilter,
  ]);

  // =======================================================
  // STATISTICS
  // =======================================================

  const totalCampaigns =
    allCampaigns.length;

  const activeCampaigns =
    allCampaigns.filter(
      (campaign) =>
        (
          campaign.status ||
          "Ongoing"
        )
          .toLowerCase() ===
        "ongoing"
    ).length;

  // IMPORTANT:
  // Do NOT sum campaign.volunteers here.
  // Campaign-wise volunteer counts are different
  // from total community volunteers.
  const totalVolunteers =
    TOTAL_COMMUNITY_VOLUNTEERS;

  const locations = new Set(
    allCampaigns
      .map(
        (campaign) =>
          campaign.location
      )
      .filter(Boolean)
  ).size;

  // =======================================================
  // SHARE
  // =======================================================

  const handleShare = async (
    campaign
  ) => {
    const shareData = {
      title:
        campaign.title ||
        "EcoVibe Campaign",

      text:
        campaign.description ||
        "EcoVibe Environmental Campaign",

      url:
        window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(
          shareData
        );
      } catch (error) {
        console.log(
          "Share cancelled:",
          error
        );
      }
    } else {
      try {
        await navigator.clipboard.writeText(
          window.location.href
        );

        alert(
          "Campaign link copied to clipboard!"
        );
      } catch (error) {
        console.error(
          "Clipboard Error:",
          error
        );

        alert(
          "Unable to copy campaign link."
        );
      }
    }
  };

  // =======================================================
  // RENDER
  // =======================================================

  return (
    <>
      <Navbar />

      <SecondNavbar />

      {/* =================================================
          MAIN PAGE
      ================================================= */}

      <main className="campaign-page">

        {/* =================================================
            HEADER
        ================================================= */}

        <section className="campaign-header">
          <div className="campaign-header-inner">

            {/* BREADCRUMB */}

            <div className="breadcrumb">

              <span>
                EcoVibe
              </span>

              <span className="breadcrumb-arrow">
                ›
              </span>

              <span>
                Community Programs
              </span>

              <span className="breadcrumb-arrow">
                ›
              </span>

              <strong>
                Campaigns
              </strong>

            </div>

            {/* HEADER CONTENT */}

            <div className="header-content">

              <div className="header-text">

                <div className="section-tag">
                  COMMUNITY & ENVIRONMENT
                </div>

                <h1>
                  Environmental Campaigns
                </h1>

                <p>
                  Explore approved
                  environmental
                  initiatives,
                  community programs,
                  and awareness
                  campaigns organized
                  through EcoVibe.
                </p>

              </div>

              <div className="header-action">

                <Link to="/create-campaign">
                  <button
                    type="button"
                    className="primary-action"
                  >
                    <span>
                      +
                    </span>

                    Create Campaign
                  </button>
                </Link>

              </div>

            </div>

          </div>
        </section>

        {/* =================================================
            STATISTICS
        ================================================= */}

        <section className="campaign-stats">

          <div className="stats-grid">

            {/* TOTAL CAMPAIGNS */}

            <div className="stat-card">

              <div className="stat-icon green">
                <span>
                  ▦
                </span>
              </div>

              <div>

                <span className="stat-label">
                  Total Campaigns
                </span>

                <strong>
                  {totalCampaigns}
                </strong>

              </div>

            </div>

            {/* ACTIVE CAMPAIGNS */}

            <div className="stat-card">

              <div className="stat-icon blue">
                <span>
                  ●
                </span>
              </div>

              <div>

                <span className="stat-label">
                  Active Campaigns
                </span>

                <strong>
                  {activeCampaigns}
                </strong>

              </div>

            </div>

            {/* COMMUNITY VOLUNTEERS */}

            <div className="stat-card">

              <div className="stat-icon orange">
                <span>
                  ♟
                </span>
              </div>

              <div>

                <span className="stat-label">
                  Community Volunteers
                </span>

                <strong>
                  {totalVolunteers}
                </strong>

              </div>

            </div>

            {/* LOCATIONS */}

            <div className="stat-card">

              <div className="stat-icon purple">
                <span>
                  ⌖
                </span>
              </div>

              <div>

                <span className="stat-label">
                  Locations Covered
                </span>

                <strong>
                  {locations}
                </strong>

              </div>

            </div>

          </div>

        </section>

        {/* =================================================
            SEARCH / FILTER TOOLBAR
        ================================================= */}

        <section className="campaign-toolbar">

          <div className="toolbar-left">

            <div className="toolbar-heading">

              <h2>
                Campaign Directory
              </h2>

              <span>
                {
                  filteredCampaigns.length
                }{" "}
                campaigns
              </span>

            </div>

          </div>

          <div className="toolbar-right">

            {/* SEARCH */}

            <div className="campaign-search">

              <span className="search-icon">
                ⌕
              </span>

              <input
                type="text"
                placeholder="Search campaigns..."
                value={search}
                onChange={(e) =>
                  setSearch(
                    e.target.value
                  )
                }
              />

              {search && (
                <button
                  type="button"
                  className="clear-search"
                  onClick={() =>
                    setSearch("")
                  }
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}

            </div>

            {/* STATUS FILTER */}

            <select
              className="status-filter"
              value={
                statusFilter
              }
              onChange={(e) =>
                setStatusFilter(
                  e.target.value
                )
              }
            >
              <option value="All">
                All Status
              </option>

              <option value="Ongoing">
                Ongoing
              </option>

              <option value="Completed">
                Completed
              </option>

              <option value="Upcoming">
                Upcoming
              </option>
            </select>

          </div>

        </section>

        {/* =================================================
            CAMPAIGN CONTENT
        ================================================= */}

        <section className="campaign-content-area">

          {/* LOADING */}

          {loading ? (

            <div className="campaign-loading">

              <div className="loading-spinner"></div>

              <p>
                Loading approved
                campaigns...
              </p>

            </div>

          ) : filteredCampaigns.length ===
            0 ? (

            /* NO RESULTS */

            <div className="no-campaign">

              <div className="no-campaign-icon">
                🌱
              </div>

              <h2>
                No Campaigns Found
              </h2>

              <p>
                No campaigns match
                your current search
                or filter. Try
                another keyword.
              </p>

              <button
                type="button"
                className="reset-btn"
                onClick={() => {
                  setSearch("");
                  setStatusFilter(
                    "All"
                  );
                }}
              >
                Reset Filters
              </button>

            </div>

          ) : (

            /* CAMPAIGN GRID */

            <div className="campaign-grid">

              {filteredCampaigns.map(
                (campaign) => {

                  const status =
                    campaign.status ||
                    "Ongoing";

                  return (
                    <article
                      className="campaign-card"
                      key={
                        campaign._id ||
                        campaign.id
                      }
                    >

                      {/* =================================
                          IMAGE
                      ================================= */}

                      <div className="campaign-card-image">

                        <img
                          src={getImageUrl(
                            campaign.image
                          )}
                          alt={
                            campaign.title ||
                            "Environmental Campaign"
                          }
                          onError={(e) => {
                            e.currentTarget.src =
                              "https://images.unsplash.com/photo-1497250681960-ef046c08a56e?w=1200";
                          }}
                        />

                        <div className="image-overlay"></div>

                        <span
                          className={`status-badge ${String(
                            status
                          )
                            .toLowerCase()
                            .replace(
                              /\s+/g,
                              "-"
                            )}`}
                        >

                          <span className="status-dot"></span>

                          {status}

                        </span>

                      </div>

                      {/* =================================
                          CARD BODY
                      ================================= */}

                      <div className="campaign-card-body">

                        <div className="campaign-card-top">

                          <span className="campaign-type">
                            ENVIRONMENTAL PROGRAM
                          </span>

                        </div>

                        <h3>
                          {campaign.title}
                        </h3>

                        {/* =================================
                            META
                        ================================= */}

                        <div className="campaign-meta">

                          {/* LOCATION */}

                          <div className="meta-item">

                            <span className="meta-icon">
                              ⌖
                            </span>

                            <div>

                              <small>
                                LOCATION
                              </small>

                              <strong>
                                {
                                  campaign.location ||
                                  "N/A"
                                }
                              </strong>

                            </div>

                          </div>

                          {/* DATE */}

                          <div className="meta-item">

                            <span className="meta-icon">
                              ◷
                            </span>

                            <div>

                              <small>
                                DATE
                              </small>

                              <strong>
                                {formatDate(
                                  campaign.date
                                )}
                              </strong>

                            </div>

                          </div>

                          {/* CAMPAIGN VOLUNTEERS */}

                          <div className="meta-item">

                            <span className="meta-icon">
                              ♟
                            </span>

                            <div>

                              <small>
                                VOLUNTEERS
                              </small>

                              <strong>
                                {
                                  Number(
                                    campaign.volunteers ||
                                      0
                                  )
                                }
                              </strong>

                            </div>

                          </div>

                          {/* ORGANIZER */}

                          <div className="meta-item">

                            <span className="meta-icon">
                              ◎
                            </span>

                            <div>

                              <small>
                                ORGANIZER
                              </small>

                              <strong>
                                {
                                  campaign.organizer ||
                                  "EcoVibe"
                                }
                              </strong>

                            </div>

                          </div>

                        </div>

                        {/* =================================
                            DESCRIPTION
                        ================================= */}

                        <div className="campaign-description">

                          <h4>
                            About Campaign
                          </h4>

                          <p>
                            {
                              campaign.description ||
                              "No campaign description available."
                            }
                          </p>

                        </div>

                        {/* =================================
                            FOOTER
                        ================================= */}

                        <div className="campaign-card-footer">

                          <span className="verified-campaign">

                            <span>
                              ✓
                            </span>

                            Approved Initiative

                          </span>

                          <button
                            type="button"
                            className="share-btn"
                            onClick={() =>
                              handleShare(
                                campaign
                              )
                            }
                          >
                            Share

                            <span>
                              ↗
                            </span>

                          </button>

                        </div>

                      </div>

                    </article>
                  );
                }
              )}

            </div>
          )}

        </section>

      </main>

      <Footer />
    </>
  );
};

export default Campaign;

